import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Search, Sparkles, Loader2 } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "fashion", label: "Fashion" },
  { key: "home", label: "Home Decor" },
  { key: "electronics", label: "Electronics" },
];

export default function Shop() {
  const { category = "all" } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [searchMode, setSearchMode] = useState(null); // null | "smart"
  const [searching, setSearching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setSearchMode(null);
    const { data } = await api.get(`/products?category=${category}`);
    setProducts(data.products);
    setLoading(false);
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const smartSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) { load(); return; }
    setSearching(true);
    setSearchMode("smart");
    try {
      const { data } = await api.post("/search/smart", { query });
      setProducts(data.products);
    } finally {
      setSearching(false);
    }
  };

  const catLabel = CATEGORIES.find((c) => c.key === category)?.label || "All";

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-12 pb-24">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="font-mono-brand text-xs uppercase tracking-[0.3em] text-neutral-500">/ department</div>
          <h1 className="font-display font-black text-5xl md:text-7xl leading-none tracking-tighter mt-2">{catLabel}.</h1>
        </div>

        <form onSubmit={smartSearch} className="flex items-center gap-2 border-b border-black/20 focus-within:border-black w-full md:w-96">
          <Sparkles className="w-4 h-4 text-[#FF3B30]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Try: warm winter fabrics, gift under $300…"
            className="flex-1 bg-transparent py-2 outline-none text-sm placeholder:text-neutral-400"
            data-testid="smart-search-input"
          />
          <button type="submit" className="font-mono-brand text-[10px] uppercase tracking-[0.2em] px-2 py-1 hover:text-[#FF3B30]" data-testid="smart-search-btn">
            {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : "AI →"}
          </button>
        </form>
      </div>

      <div className="flex gap-6 mb-10 border-b border-black/10">
        {CATEGORIES.map((c) => (
          <a
            key={c.key}
            href={`/shop/${c.key}`}
            className={`font-mono-brand text-xs uppercase tracking-[0.2em] pb-3 border-b-2 transition-colors ${
              category === c.key ? "border-black text-black" : "border-transparent text-neutral-500 hover:text-black"
            }`}
            data-testid={`filter-${c.key}`}
          >
            {c.label}
          </a>
        ))}
      </div>

      {searchMode === "smart" && (
        <div className="mb-6 font-mono-brand text-xs uppercase tracking-[0.2em] text-[#FF3B30]" data-testid="smart-search-badge">
          Nova.smart-search // "{query}"
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-neutral-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : products.length === 0 ? (
        <p className="text-neutral-500">No products found.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
